import React, { useEffect, useState } from "react";
import { Card, Space, Tabs, Typography, Row, Col } from "antd";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { BellOutlined, SearchOutlined, SyncOutlined, KeyOutlined, SettingOutlined } from "@ant-design/icons";
import { IntegrationsService } from "../../services/IntegrationsService";
import { usePermissions } from "../../PermissionsProvider";
import DevelopersIntegrations from "./DevelopersIntegrations";
import NotificationSettings from "./NotificationSettings";
import DevelopersAPIKeys from "./DevelopersAPIKeys";

const { TabPane } = Tabs;

const DevelopersBase: React.FC = () => {
  const { apiKey } = usePermissions();
  const location = useLocation(); // Access location object
  const [activeTab, setActiveTab] = useState<string>("2"); // Default to API Keys tab
  const [slackCode, setSlackCode] = useState<string | null>(null);

  // Styles object to maintain consistent styling
  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    tabIcon: {
      marginRight: "8px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
  };

  // Map hash to tab keys
  const tabKeys: any = {
    overview: "1",
    apikeys: "2",
    webhooks: "3",
    integrations: "4",
    logs: "5",
    notifications: "6",
  };

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search); // Parse query parameters
    const hash = location.hash.replace("#", "");

    setActiveTab(tabKeys[hash] || "2"); // Default to 'apikeys' if hash is not recognized

    // Extract Slack OAuth code if present
    if (parsedQuery.code) {
      setSlackCode(parsedQuery.code as string);
      // You can now send this code to your backend for further processing
    }
  }, [location]);

  useEffect(() => {
    if (slackCode) {
      // Process the Slack code, e.g., send it to your backend
      IntegrationsService.handleSlackAuthSuccess(
        slackCode,
        null,
        apiKey
      ).subscribe(
        (res: any) => {
          console.log(`Success authenticating with Slack! ${res}`);
        },
        (error: any) => {
          console.log(`Error authenticating with Slack! ${error}`);
        }
      );
    }
  }, [slackCode]);

  // Add global CSS for interactive rows
  useEffect(() => {
    // Add styles for interactive rows
    const style = document.createElement('style');
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="settings-container" style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                Settings
              </Typography.Title>
              <Typography.Text type="secondary">
                Manage your API keys, integrations, and notification preferences
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab} 
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}
      >
        <TabPane 
          tab={
            <span style={{ padding: "0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <KeyOutlined style={styles.tabIcon} />
              API Keys
            </span>
          } 
          key="2"
        >
          <div style={styles.contentCard}>
            <DevelopersAPIKeys />
          </div>
        </TabPane>
        
        <TabPane 
          tab={
            <span style={{ padding: "0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <SyncOutlined style={styles.tabIcon} />
              Integrations
            </span>
          } 
          key="4"
        >
          <div style={styles.contentCard}>
            <DevelopersIntegrations />
          </div>
        </TabPane>
        
        <TabPane 
          tab={
            <span style={{ padding: "0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <BellOutlined style={styles.tabIcon} />
              Notifications
            </span>
          } 
          key="6"
        >
          <div style={styles.contentCard}>
            <NotificationSettings />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default DevelopersBase;
